import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, Spin, message, Tooltip } from 'antd';
import { IonIcon } from '@ionic/react';
import { closeOutline, downloadOutline, eyeOutline, copyOutline, refreshOutline, personOutline, brushOutline } from 'ionicons/icons';
import { downloadSVGFromStorage, getUserDisplayName, loadPublicStitchState /* incrementViewOrDownloadCount */ } from '../../../lib/firebase/firebase'; // View count tracking removed - not needed currently
import AppContext from '../../../contexts/AppContext';
import { useCopyToClipboard } from 'usehooks-ts';
import { useDispatch } from 'react-redux';
import { setCreatorFilter, clearSearch } from '../../../redux/slices/CommunityFiles';
import { setShowEditor } from '../../../redux/slices/MyFiles';
import { isEmbroideryTrace } from '../../../config/appVariant';
import { getStitchDifficulty } from '../../../utils/stitchDifficulty';
import { describeStitchTypes } from '../../../utils/stitchLayerTypes';
import StitchPreview from '../../StitchEditor/StitchPreview';
import { useAuth } from '../../../contexts/AuthContext';
import './CommunityImageModal.css';

// Helper function to generate Reddit-style username from UID
const generateRedditStyleName = (uid) => {
  const adjectives = [
    'Curious', 'Wise', 'Brave', 'Swift', 'Clever', 'Bold', 'Gentle', 'Fierce', 'Noble', 'Witty',
    'Calm', 'Bright', 'Mystic', 'Silent', 'Golden', 'Silver', 'Crimson', 'Azure', 'Emerald', 'Violet',
    'Ancient', 'Modern', 'Cosmic', 'Electric', 'Frozen', 'Burning', 'Dancing', 'Flying', 'Hidden', 'Shining'
  ];
  
  const animals = [
    'Panda', 'Koala', 'Eagle', 'Wolf', 'Tiger', 'Dolphin', 'Owl', 'Fox', 'Bear', 'Lion',
    'Hawk', 'Whale', 'Deer', 'Rabbit', 'Turtle', 'Penguin', 'Falcon', 'Swan', 'Raven', 'Lynx',
    'Otter', 'Seal', 'Shark', 'Octopus', 'Phoenix', 'Dragon', 'Unicorn', 'Griffin', 'Sphinx', 'Pegasus'
  ];

  const uidHash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  const adjIndex = uidHash % adjectives.length;
  const animalIndex = (uidHash * 7) % animals.length;
  const number = (uidHash % 999) + 1;
  
  return `${adjectives[adjIndex]}${animals[animalIndex]}${number}`;
};

const CommunityImageModal = ({ isOpen, onClose, file }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [artistName, setArtistName] = useState(null);
  const [loadingArtist, setLoadingArtist] = useState(false);
  const [openingInEditor, setOpeningInEditor] = useState(false);
  const [stitchState, setStitchState] = useState(null);
  const [stitchPreviewReady, setStitchPreviewReady] = useState(false);
  // Measured off the flat image so the stitch render can take over its exact
  // box instead of resizing the design when it appears.
  const [imageNaturalSize, setImageNaturalSize] = useState(null);
  const { setSvgStore } = useContext(AppContext);
  const [, copy] = useCopyToClipboard();
  const dispatch = useDispatch();
  const { user } = useAuth();

  // Fetch artist display name when file changes
  useEffect(() => {
    const fetchArtistName = async () => {
      if (file?.ownerUid && isOpen) {
        setLoadingArtist(true);
        try {
          const displayName = await getUserDisplayName(file.ownerUid);
          setArtistName(displayName);
        } catch (error) {
          console.error('Error fetching artist name:', error);
          setArtistName(null);
        } finally {
          setLoadingArtist(false);
        }
      }
    };

    fetchArtistName();
  }, [file?.ownerUid, isOpen]);

  // Load published settings only when opening a design. Large geometry uses
  // additional chunk documents, keeping it out of the gallery's metadata feed.
  // The same complete state drives both stitch labels and the preview.
  useEffect(() => {
    setStitchState(null);
    setStitchPreviewReady(false);
    setImageNaturalSize(null);
    if (!isOpen || !isEmbroideryTrace || !file?.id) {
      setStitchState(null);
      return;
    }
    let cancelled = false;
    loadPublicStitchState(file.id).then((state) => {
      if (!cancelled) setStitchState(state || null);
    });
    return () => { cancelled = true; };
  }, [isOpen, file?.id]);

  if (!file) return null;

  const difficulty = getStitchDifficulty(file.stitchStats);
  const stitchTypes = describeStitchTypes(stitchState);



  // Opens the design as a preview rather than copying it into the user's
  // account. Nothing is written: they can resize it, generate a stitch file and
  // download it, and only pay for storage in their account if they decide to
  // keep it. See StitchEditorPage for how the preview suppresses writes.
  const handleOpenInEditor = async () => {
    if (!user) {
      message.info('Sign in to open this design in the editor.');
      return;
    }
    setOpeningInEditor(true);
    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`Could not fetch design (${response.status})`);
      const svgText = await response.text();

      // Home renders the editor only while svgStore is defined, so this has to be
      // set — but deliberately without a `firebase` ref. useSaveSVG saves into
      // svgStore.firebase when present, so a ref left over from a previously
      // opened file would point a save at the wrong design entirely.
      setSvgStore({ svg: svgText });

      dispatch(setShowEditor({
        showEditor: true,
        currentFile: {
          // Synthetic id so nothing keyed by file id — stitch state, the AI
          // analysis cache, prompt flags — can collide with a real document.
          id: `community-preview:${file.id}`,
          readOnly: true,
          previewOf: file,
          svgData: svgText,
          name: file.seoTitle || file.name || 'Community design',
          workflow: 'stitch',
          ownerUid: file.ownerUid,
          isPublic: true,
        },
      }));
      onClose();
    } catch (error) {
      console.error('Error opening design in editor:', error);
      message.error('Could not open this design. Please try again.');
    } finally {
      setOpeningInEditor(false);
    }
  };

  const handleCopyPrompt = (prompt) => {
    copy(prompt);
    message.success('Prompt copied to clipboard!');
  };

  const handleSeeMoreFromCreator = () => {
    if (file?.ownerUid) {
      // Clear any existing search first
      dispatch(clearSearch());
      
      // Set the creator filter
      dispatch(setCreatorFilter({
        uid: file.ownerUid,
        displayName: artistName || `User ${file.ownerUid.substring(0, 8)}`
      }));
      
      // Close the modal
      onClose();
      
      message.success(`Showing artwork by ${artistName || generateRedditStyleName(file.ownerUid)}`);
    }
  };

  const handleDownload = async () => {
    try {
      // Download the SVG file directly from the URL
      const response = await fetch(file.url);
      const svgContent = await response.text();
      
      const blob = new Blob([svgContent], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `${file.name || 'community-svg'}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('SVG downloaded successfully!');
      // Download count tracking removed - not needed currently
      // await incrementViewOrDownloadCount(file.id, 'downloads');
    } catch (error) {
      console.error('Error downloading SVG:', error);
      message.error('Failed to download SVG. Please try again.');
    }
  };

  const formatDate = (createdAt) => {
    if (!createdAt) return 'Unknown date';
    
    let dateObj;
    if (typeof createdAt === 'object' && createdAt.seconds) {
      dateObj = new Date(createdAt.seconds * 1000);
    } else {
      dateObj = new Date(createdAt);
    }
    
    if (isNaN(dateObj.getTime())) return 'Unknown date';
    
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      className="community-image-modal"
      centered
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      destroyOnClose={true}
      maskClosable={true}
      closable={false}
      style={{ maxWidth: '1000px' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="community-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="community-modal-header">
          <div className="community-modal-title">
          </div>
          <button 
            className="community-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        {/* Main Content */}
        <div className="community-modal-body">
          {/* Image Preview */}
          <div className="community-image-container">
            {!imageLoaded && !stitchPreviewReady && (
              <div className="community-image-loading">
                <Spin size="large" />
              </div>
            )}
            {/* The flat artwork carries the modal until the stitch render is
                ready, and stays for good if it never is. */}
            <img
              src={file.url}
              alt={file.name}
              className="community-image"
              onLoad={(e) => {
                setImageLoaded(true);
                const { naturalWidth, naturalHeight } = e.currentTarget;
                if (naturalWidth && naturalHeight) {
                  setImageNaturalSize({ width: naturalWidth, height: naturalHeight });
                }
              }}
              style={{ display: imageLoaded && !stitchPreviewReady ? 'block' : 'none' }}
            />
            {isEmbroideryTrace && (
              <StitchPreview
                svgUrl={file.url}
                stitchState={stitchState}
                intrinsicWidth={imageNaturalSize?.width}
                intrinsicHeight={imageNaturalSize?.height}
                onReady={() => setStitchPreviewReady(true)}
                className="community-stitch-preview"
              />
            )}
          </div>

          {/* Metadata Sidebar */}
          <div className="community-metadata">
            <div className="community-info-section">
              <h3 className="community-info-title">Design Details</h3>
              
              {/* SEO Title */}
              {file.seoTitle && (
                <div className="community-info-item">
                  <span className="community-info-label">Title:</span>
                  <span className="community-info-value community-seo-title">{file.seoTitle}</span>
                </div>
              )}

              {/* SEO Description */}
              {file.seoDescription && (
                <div className="community-info-item">
                  <span className="community-info-label">Description:</span>
                  <p className="community-info-description">{file.seoDescription}</p>
                </div>
              )}
              
              <div className="community-info-item">
                <span className="community-info-label">Created:</span>
                <span className="community-info-value">{formatDate(file.createdAt)}</span>
              </div>

              {/* Stitch stats from the creator's last export. Indicative only —
                  the recipient regenerates at their own size, which changes them. */}
              {file.stitchStats?.num_stitches && (
                <div className="community-info-item">
                  <span className="community-info-label">Creator's export:</span>
                  <span className="community-info-value">
                    {file.stitchStats.num_stitches.toLocaleString()} stitches
                    {file.stitchStats.num_colors ? ` · ${file.stitchStats.num_colors} colors` : ''}
                  </span>
                </div>
              )}

              {stitchTypes.length > 0 && (
                <div className="community-info-item">
                  <span className="community-info-label">Stitch types:</span>
                  <span className="community-info-value">
                    {stitchTypes.map((type) => (
                      <span
                        key={type}
                        style={{
                          display: 'inline-block',
                          background: 'rgba(6,182,212,0.1)',
                          border: '1px solid rgba(6,182,212,0.25)',
                          borderRadius: '999px',
                          padding: '1px 8px',
                          marginRight: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}
                      >
                        {type}
                      </span>
                    ))}
                  </span>
                </div>
              )}

              {difficulty && (
                <div className="community-info-item">
                  <span className="community-info-label">To sew:</span>
                  <span className="community-info-value">
                    <span
                      style={{
                        color: difficulty.color,
                        background: difficulty.background,
                        border: '1px solid rgba(0,0,0,0.06)',
                        borderRadius: '999px',
                        padding: '1px 8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        marginRight: '6px',
                      }}
                    >
                      {difficulty.label}
                    </span>
                    {difficulty.detail}
                  </span>
                </div>
              )}

              {file.prompt && (
                <div className="community-info-item">
                  <div className="community-prompt-header">
                    <span className="community-info-label">Original Prompt:</span>
                    <Tooltip title="Copy prompt" color="#000">
                      <button 
                        className="community-copy-button"
                        onClick={() => handleCopyPrompt(file.prompt)}
                      >
                        <IonIcon icon={copyOutline} />
                      </button>
                    </Tooltip>
                  </div>
                  <p className="community-info-description community-prompt-text">{file.prompt}</p>
                </div>
              )}

              {file.ownerUid && (
                <div className="community-info-item">
                  <span className="community-info-label">Artist:</span>
                  <span className="community-info-value community-artist-credit">
                    {loadingArtist ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Spin size="small" />
                        Loading...
                      </span>
                    ) : (
                      artistName || generateRedditStyleName(file.ownerUid)
                    )}
                  </span>
                </div>
              )}

              {/* Search Keywords */}
              {file.searchKeywords && file.searchKeywords.length > 0 && (
                <div className="community-info-item">
                  <span className="community-info-label">Tags:</span>
                  <div className="community-tags">
                    {file.searchKeywords.slice(0, 6).map((keyword, index) => (
                      <span key={index} className="community-tag">{keyword}</span>
                    ))}
                  </div>
                </div>
              )}

              {(file.views || file.downloads) && (
                <div className="community-stats">
                  {file.views && (
                    <div className="community-stat">
                      <IonIcon icon={eyeOutline} />
                      <span>{file.views} views</span>
                    </div>
                  )}
                  {file.downloads && (
                    <div className="community-stat">
                      <IonIcon icon={downloadOutline} />
                      <span>{file.downloads} downloads</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="community-actions">
              {isEmbroideryTrace && (
                <Button
                  type="primary"
                  icon={<IonIcon icon={brushOutline} />}
                  onClick={handleOpenInEditor}
                  loading={openingInEditor}
                  className="community-action-primary"
                >
                  {openingInEditor ? 'Opening…' : 'Open embroidery design'}
                </Button>
              )}

              {/* SvgTrace only — there the SVG is the deliverable. On
                  EmbroideryTrace it's just the raw artwork behind the design,
                  which needs stitch settings and a hoop size to be worth
                  anything, so "Open embroidery design" is the only useful way in. */}
              {!isEmbroideryTrace && (
                <Button
                  type="primary"
                  icon={<IonIcon icon={downloadOutline} />}
                  onClick={handleDownload}
                  className="community-action-secondary"
                >
                  Download SVG
                </Button>
              )}

              {file.ownerUid && (
                <Button
                  type="primary"
                  icon={<IonIcon icon={personOutline} />}
                  onClick={handleSeeMoreFromCreator}
                  className="community-action-creator"
                  disabled={loadingArtist}
                >
                  See more from creator
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CommunityImageModal; 